import React from 'react';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';

import DetailsSectionCard, { SECTION_BORDER } from './DetailsSectionCard';
import Pill from './Pill';
import formatDate from './formatDate';

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(0, 2fr) minmax(0, 1.6fr) minmax(0, 2.4fr) minmax(0, 1.5fr) minmax(0, 1.5fr) 96px',
    gap: '16px',
    padding: theme.spacing(0, 3),
    minHeight: 48,
    alignItems: 'center',
    fontSize: 14,
    borderBottom: SECTION_BORDER,
    '&:last-child': {
      borderBottom: 'none',
    },
  },

  head: {
    minHeight: 36,
    backgroundColor: theme.palette.background.default,
    fontSize: 12,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: theme.palette.secondary.contrastText,
  },

  deleted: {
    color: theme.palette.secondary.contrastText,
  },

  address: {
    fontWeight: 'bold',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1, 0),
  },

  addressExtra: {
    fontWeight: 400,
  },

  dates: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1, 0),
  },

  id: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  secondary: {
    color: theme.palette.text.secondary,
  },

  empty: {
    padding: theme.spacing(2, 3),
  },
}));

interface Props {
  warehouses: any[];
}

const WarehousesSection = ({ warehouses }: Props) => {
  const classes = useStyles();

  const list = warehouses ?? [];
  const deletedCount = list.filter((warehouse) => !!warehouse.deletedAt).length;
  const activeCount = list.length - deletedCount;

  return (
    <DetailsSectionCard
      id="user-details-warehouses"
      title="Warehouses"
      count={list.length}
      summary={list.length > 0 ? `${activeCount} active · ${deletedCount} deleted` : undefined}>
      {list.length === 0 ? (
        <Typography className={classes.empty} color="textSecondary">
          No warehouses found
        </Typography>
      ) : (
        <div role="table" aria-label="Warehouses">
          <div role="row" className={classNames(classes.row, classes.head)}>
            <span role="columnheader">Address</span>
            <span role="columnheader">City</span>
            <span role="columnheader">ID</span>
            <span role="columnheader">Created</span>
            <span role="columnheader">Updated / Reviewed</span>
            <span role="columnheader">Status</span>
          </div>
          {list.map((warehouse: any) => {
            const isDeleted = !!warehouse.deletedAt;
            const mutedClass = isDeleted ? classes.deleted : classes.secondary;

            return (
              <div
                key={warehouse.id}
                role="row"
                className={classNames(classes.row, isDeleted && classes.deleted)}>
                <span role="cell" className={classes.address}>
                  <span>{warehouse.addressLine1}</span>
                  {warehouse.addressLine2 && (
                    <span className={classes.addressExtra}>{warehouse.addressLine2}</span>
                  )}
                  {warehouse.addressLine3 && (
                    <span className={classes.addressExtra}>{warehouse.addressLine3}</span>
                  )}
                </span>
                <span role="cell">
                  {warehouse.city}, {warehouse.stateCode} {warehouse.postalCode}
                </span>
                <span
                  role="cell"
                  className={classNames(classes.id, mutedClass)}
                  title={warehouse.id}>
                  {warehouse.id}
                </span>
                <span role="cell" className={mutedClass}>
                  {formatDate(warehouse.createdAt) ?? '—'}
                </span>
                <span role="cell" className={classNames(classes.dates, mutedClass)}>
                  <span>{formatDate(warehouse.updatedAt) ?? '—'}</span>
                  {warehouse.reviewedAt && (
                    <span>Reviewed: {formatDate(warehouse.reviewedAt)}</span>
                  )}
                </span>
                <span role="cell">{isDeleted && <Pill variant="danger">Deleted</Pill>}</span>
              </div>
            );
          })}
        </div>
      )}
    </DetailsSectionCard>
  );
};

export default WarehousesSection;
